import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarDays, RefreshCw, MapPin, Clock, User, ChevronLeft, ChevronRight } from "lucide-react";

const SOURCE_META = {
  lim_booking_slip: { label: "Studio slip", tone: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  lim_booking: { label: "Studio booking", tone: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  lim_shoot: { label: "Pre-shoot checklist", tone: "bg-blue-500/10 text-blue-300 border-blue-500/30" },
  lime_external: { label: "LIME Studios feed", tone: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
};

function startOfWeekMonday(d) {
  const base = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = base.getDay(); // 0=Sun … 6=Sat
  const offset = dow === 0 ? -6 : 1 - dow;
  base.setDate(base.getDate() + offset);
  return base;
}

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function BookingsCalendar() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [weekStart, setWeekStart] = useState(() => startOfWeekMonday(new Date()));
  const canManualSync = user?.is_super_admin || user?.is_admin || (user?.role === "manager" && user?.team === "Shoot Team");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/external-bookings");
      setBookings(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runSync = async () => {
    if (!canManualSync) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/external-bookings/sync");
      toast.success(`Synced ${data.fetched} · ${data.new} new · ${data.tasks_created} tasks created`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const byDay = useMemo(() => {
    const m = new Map();
    weekDays.forEach((d) => m.set(isoDate(d), []));
    bookings.forEach((b) => {
      if (!b.date) return;
      if (m.has(b.date)) m.get(b.date).push(b);
    });
    m.forEach((arr) => arr.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")));
    return m;
  }, [bookings, weekDays]);

  const upcoming = useMemo(() => {
    const today = isoDate(new Date());
    return bookings.filter((b) => b.date >= today).sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
  }, [bookings]);

  const shiftWeek = (delta) => {
    setWeekStart((cur) => {
      const d = new Date(cur);
      d.setDate(cur.getDate() + delta);
      return d;
    });
  };

  const rangeLabel = (() => {
    const start = weekDays[0];
    const end = weekDays[6];
    const fmt = (d, y) => d.toLocaleString(undefined, { month: "short", day: "numeric", ...(y ? { year: "numeric" } : {}) });
    return `${fmt(start)} – ${fmt(end, true)}`;
  })();

  return (
    <div className="p-4 sm:p-6" data-testid="bookings-calendar-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="overline flex items-center gap-2"><CalendarDays size={12} /> LIME · Shoot Prep</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter mt-1">Bookings Calendar</h1>
          <p className="text-white/50 text-sm mt-1">
            Studio bookings, pre-shoot checklists and iCal feed from Content Studio Baner — refreshed every 5 minutes.
          </p>
        </div>
        {canManualSync && (
          <Button
            onClick={runSync}
            disabled={syncing}
            data-testid="bookings-sync-btn"
            className="bg-[#E11D2E] hover:bg-[#F03041] rounded-full"
          >
            <RefreshCw size={14} className={`mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync now"}
          </Button>
        )}
      </div>

      {/* Week toggle */}
      <div className="glass rounded-xl p-3 mb-4 flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => shiftWeek(-7)} data-testid="bookings-prev-week" className="text-white/70 hover:text-white">
          <ChevronLeft size={16} />
        </Button>
        <div className="font-display text-lg tracking-tight" data-testid="bookings-week-label">{rangeLabel}</div>
        <Button variant="ghost" size="sm" onClick={() => shiftWeek(7)} data-testid="bookings-next-week" className="text-white/70 hover:text-white">
          <ChevronRight size={16} />
        </Button>
      </div>

      {/* 7-day grid */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2 mb-8">
        {weekDays.map((d, i) => {
          const key = isoDate(d);
          const items = byDay.get(key) || [];
          const isToday = key === isoDate(new Date());
          return (
            <div key={key} className={`rounded-xl border ${isToday ? "border-[#E11D2E]/50 bg-[#E11D2E]/5" : "border-white/8 bg-surface"} p-2 min-h-[140px]`}>
              <div className="text-[0.7rem] uppercase tracking-widest text-white/50 mb-1 font-mono">
                {DAY_NAMES[i]} · {d.getDate()}/{d.getMonth() + 1}
              </div>
              {items.length === 0 ? (
                <div className="text-white/25 italic text-xs">Free</div>
              ) : items.map((b) => (
                <BookingCard key={b.id} b={b} compact />
              ))}
            </div>
          );
        })}
      </div>

      {/* Upcoming list */}
      <div>
        <div className="overline mb-3">Upcoming · {upcoming.length}</div>
        {loading ? (
          <div className="text-white/40 text-sm italic">Loading bookings…</div>
        ) : upcoming.length === 0 ? (
          <div className="glass rounded-xl p-6 text-white/40 text-sm italic text-center">
            No upcoming bookings yet. New entries from LIME Shoot Prep will appear here within 5 minutes.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {upcoming.slice(0, 24).map((b) => <BookingCard key={b.id} b={b} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function BookingCard({ b, compact }) {
  const meta = SOURCE_META[b.source] || { label: b.source, tone: "bg-white/5 text-white/60 border-white/15" };
  return (
    <div
      data-testid={`booking-card-${b.id}`}
      className={`rounded-lg border ${compact ? "border-white/10 bg-black/30" : "border-white/10 bg-surface"} p-2.5`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className={`text-[0.65rem] uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${meta.tone}`}>
          {meta.label}
        </div>
        {b.task_id && (
          <span title="A task was created for this booking" className="text-[0.65rem] uppercase tracking-widest text-white/40 font-mono">Task ✓</span>
        )}
      </div>
      <div className="text-sm font-medium text-white truncate" title={b.title}>{b.customer_name || b.title}</div>
      <div className="flex items-center gap-2 text-xs text-white/60 mt-1 font-mono">
        <Clock size={10} />
        {b.date} · {b.start_time}{b.end_time ? `–${b.end_time}` : ""}
      </div>
      {!compact && b.location && (
        <div className="flex items-center gap-2 text-xs text-white/50 mt-1">
          <MapPin size={10} className="shrink-0" /> <span className="truncate">{b.location}</span>
        </div>
      )}
      {!compact && b.shoot_type && (
        <div className="text-xs text-white/50 mt-1">Type: {b.shoot_type}</div>
      )}
      {!compact && Array.isArray(b.crew) && b.crew.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-white/50 mt-1">
          <User size={10} className="shrink-0" /> <span className="truncate">{b.crew.join(", ")}</span>
        </div>
      )}
      {!compact && b.notes && (
        <div className="text-xs text-white/40 mt-1 line-clamp-2 whitespace-pre-wrap">{b.notes}</div>
      )}
    </div>
  );
}
